import { showToast } from "@/lib/toast";
import { useAuthStore } from "@/stores";
import { getLocaleFromCookie } from "@/i18n/locale-cookie";

// Localized session-expired toast strings. Inlined here (instead of routed
// through use-intl) because this handler runs from the QueryClient cache
// callbacks which live outside the React tree — useTranslations would not
// resolve. Two-locale parity matches the rest of the app's bundles.
const MESSAGES: Record<"en" | "vi", { title: string; description: string }> = {
  en: {
    title: "Your session has expired",
    description: "Please sign in again to continue.",
  },
  vi: {
    title: "Phiên đăng nhập đã hết hạn",
    description: "Vui lòng đăng nhập lại để tiếp tục.",
  },
};

// Single-flight latch. Multiple in-flight requests can fire 401 in parallel
// (a page often runs >1 query at once). Without this latch, every 401
// would push its own toast and try to redirect, creating a noisy stack
// and a race on window.location.
let alreadyHandled = false;

/**
 * Centralized handler for "session expired beyond refresh recovery."
 *
 * The worker hub-proxy already auto-refreshes on 401 — when this handler
 * fires it means BOTH the access token AND the refresh token are dead, so
 * a friendly redirect to /signin is the only path forward. Guards:
 *
 *   - latch resets only on full page reload, so a follow-up 401 from a
 *     parallel request inside the same tick gets swallowed.
 *   - guard against /signin itself (we may hit 401 from the auth probe
 *     during the signin attempt — don't infinite-loop redirect).
 */
export function handleSessionExpired(): void {
  if (alreadyHandled) return;
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/signin")) return;

  alreadyHandled = true;

  const locale = getLocaleFromCookie();
  const msg = MESSAGES[locale] ?? MESSAGES.en;
  showToast.error({ title: msg.title, description: msg.description, duration: 5000 });

  // Best-effort store reset. Don't await — the redirect must happen even
  // if the store fails to clear (logout API would 401 too).
  useAuthStore.setState({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false,
    error: null,
  });

  // Full navigation rather than router.navigate — clears every in-flight
  // SPA state, forces a clean signin page mount with no stale provider
  // state hanging around. The "from" param lets the post-login bounce
  // return the user to where they were.
  const from = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/signin?from=${from}`);
}

// Test-only escape hatch so a Vitest run can re-arm the latch between
// cases. Production code never imports this.
export function _resetSessionExpiredLatchForTest(): void {
  alreadyHandled = false;
}
